import { FC, useMemo, useState } from "react"
import {
  Alert,
  Autocomplete,
  Button,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import { Userbot } from "../../interfaces"
import {
  useCreateTriggerMutation,
  useUpdateTriggerMutation,
  useLazyClientsQuery,
} from "../../store"
import { useInfiniteSelectList } from "@/core/hooks"
import { infiniteListboxProps } from "@/core/components"
import { useCatalog, useUserbotWriteAccess } from "../../hooks"
import { ChainBuilder } from "../chains/chainBuilder"
import {
  ChainFormState,
  Descriptors,
  chainStateFromDto,
} from "../chains/chainSerializer"
import { ConditionExprEditor } from "../fields/conditionExprEditor"
import { defaultCondition } from "../fields/conditionExpr"
import {
  isLegacyJoinTrigger,
  LEGACY_JOIN_GUIDANCE,
  trafficPolicyErrorMessage,
  triggerReasonLabel,
} from "../common/trafficPolicy"
import { TRIGGER_TEMPLATE_HINTS } from "../../constants/hints"

interface IProps {
  trigger?: Userbot.Trigger
  onDone?: () => void
}

/** Преобразует ISO-строку в формат `datetime-local` (`YYYY-MM-DDTHH:mm`). */
const isoToLocalDatetime = (iso: string | null | undefined): string => {
  if (!iso) return ""
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ""
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Преобразует значение `datetime-local` в ISO-строку. */
const localDatetimeToIso = (val: string): string => {
  if (!val.trim()) return ""
  const d = new Date(val)
  if (isNaN(d.getTime())) return val
  return d.toISOString()
}

export const TriggerForm: FC<IProps> = ({ trigger, onDone }) => {
  const isEdit = !!trigger
  const canWrite = useUserbotWriteAccess()
  const [lazyClients] = useLazyClientsQuery()
  const clientsList = useInfiniteSelectList<Userbot.Client>({
    fetchPage: (p) => lazyClients(p).unwrap(),
    getId: (c) => c.id,
  })
  const catalog = useCatalog()

  const [clientId, setClientId] = useState(trigger?.clientId ?? "")
  const [type, setType] = useState(trigger?.type ?? "")
  const [match, setMatch] = useState<Userbot.ConditionExpr | null>(
    trigger?.match ?? null,
  )
  const [oneShot, setOneShot] = useState(trigger?.oneShot ?? false)
  const [isActive, setIsActive] = useState(trigger?.isActive ?? true)
  const [inviteHash, setInviteHash] = useState(trigger?.inviteHash ?? "")
  const [maxFires, setMaxFires] = useState<string>(
    trigger?.maxFires != null ? String(trigger.maxFires) : "",
  )
  const [expiresAt, setExpiresAt] = useState<string>(
    isoToLocalDatetime(trigger?.expiresAt),
  )
  const [template, setTemplate] = useState<Userbot.ChainTemplate | null>(null)
  const [error, setError] = useState<string | null>(null)

  const descriptorsMap = useMemo<Descriptors>(
    () =>
      catalog.actions.reduce((acc, d) => {
        acc[d.name] = d
        return acc
      }, {} as Descriptors),
    [catalog.actions],
  )

  const initialBuilderState: ChainFormState | undefined = trigger
    ? chainStateFromDto(
        trigger.chainTemplate as Partial<Userbot.ChainDto>,
        descriptorsMap,
      )
    : undefined

  const [createTrigger, createState] = useCreateTriggerMutation()
  const [updateTrigger, updateState] = useUpdateTriggerMutation()
  const loading = createState.isLoading || updateState.isLoading

  const handleSubmit = async () => {
    setError(null)
    const errs: string[] = []
    if (!isEdit && !clientId) errs.push("Выберите клиента")
    if (!type.trim()) errs.push("Укажите тип матчера")
    if (isLegacyJoinTrigger(type) && (!isEdit || isActive))
      errs.push(LEGACY_JOIN_GUIDANCE)
    if (inviteHash && !/\S/.test(inviteHash)) {
      errs.push("inviteHash не может состоять только из пробелов")
    }
    const maxFiresNum = maxFires.trim() === "" ? null : Number(maxFires)
    if (maxFiresNum != null && (Number.isNaN(maxFiresNum) || maxFiresNum < 0)) {
      errs.push("maxFires должен быть неотрицательным числом")
    }
    if (!template) errs.push("Заполните шаблон цепочки корректно")
    if (errs.length) {
      setError(errs.join("\n"))
      return
    }

    const matchExpr = match ?? defaultCondition()
    const expiresAtIso = localDatetimeToIso(expiresAt)

    try {
      if (isEdit) {
        await updateTrigger({
          id: trigger!.id,
          body: {
            match: matchExpr,
            oneShot,
            isActive,
            chainTemplate: template as unknown as Record<string, unknown>,
            inviteHash: inviteHash.trim() || null,
            maxFires: maxFiresNum ?? undefined,
            expiresAt: expiresAtIso || undefined,
          },
        }).unwrap()
      } else {
        await createTrigger({
          body: {
            clientId,
            type: type.trim(),
            match: matchExpr,
            oneShot,
            chainTemplate: template as unknown as Record<string, unknown>,
            inviteHash: inviteHash.trim() || undefined,
            maxFires: maxFiresNum ?? undefined,
            expiresAt: expiresAtIso || undefined,
          },
        }).unwrap()
      }
      onDone?.()
    } catch (e) {
      setError(trafficPolicyErrorMessage(e))
    }
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {trigger?.nonExecutableReason && (
        <Alert severity="warning">
          {triggerReasonLabel(trigger.nonExecutableReason)}
        </Alert>
      )}
      {isLegacyJoinTrigger(type) && (
        <Alert severity="warning">{LEGACY_JOIN_GUIDANCE}</Alert>
      )}

      {!isEdit && (
        <Autocomplete<Userbot.Client>
          options={clientsList.items}
          value={clientsList.items.find((c) => c.id === clientId) ?? null}
          onChange={(_, v) => setClientId(v?.id ?? "")}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          filterOptions={(o) => o}
          onInputChange={(_, v, reason) => {
            if (reason === "input") clientsList.setSearch(v)
          }}
          renderInput={(params) => (
            <TextField {...params} label="Клиент (наблюдаемый)" required />
          )}
          {...infiniteListboxProps({
            hasMore: clientsList.hasMore,
            isFetching: clientsList.isFetching,
            onLoadMore: clientsList.loadMore,
          })}
          renderOption={(props, option, state) =>
            [props, option.name, state] as React.ReactNode
          }
        />
      )}

      <Autocomplete<string, false, false, true>
        freeSolo
        options={catalog.matchers
          .map((m) => m.type)
          .filter((type) => !isLegacyJoinTrigger(type))}
        value={type}
        onInputChange={(_, v) => setType(v)}
        disabled={isEdit}
        renderInput={(params) => (
          <TextField {...params} label="Тип матчера" required />
        )}
      />

      <Divider textAlign="left">
        <Typography variant="caption">Фильтр match</Typography>
      </Divider>

      <ConditionExprEditor
        value={match}
        onChange={setMatch}
        headHints={TRIGGER_TEMPLATE_HINTS}
        limits={catalog.limits}
      />

      <FormControlLabel
        control={
          <Switch
            checked={oneShot}
            onChange={(e) => setOneShot(e.target.checked)}
          />
        }
        label="Однократный (oneShot)"
      />
      {isEdit && (
        <FormControlLabel
          control={
            <Switch
              checked={isActive}
              disabled={!isActive && isLegacyJoinTrigger(type)}
              onChange={(e) => {
                if (e.target.checked && isLegacyJoinTrigger(type)) return
                setIsActive(e.target.checked)
              }}
            />
          }
          label="Активен"
        />
      )}

      <Divider textAlign="left">
        <Typography variant="subtitle2">Lifecycle</Typography>
      </Divider>
      <TextField
        label="Invite hash"
        value={inviteHash}
        onChange={(e) => setInviteHash(e.target.value)}
        fullWidth
        helperText="Хэш инвайт-ссылки (если триггер по инвайту)"
      />
      <TextField
        label="Max срабатываний"
        type="number"
        value={maxFires}
        onChange={(e) => setMaxFires(e.target.value)}
        fullWidth
        helperText="Пусто = без лимита"
      />
      <TextField
        label="Истекает"
        type="datetime-local"
        value={expiresAt}
        onChange={(e) => setExpiresAt(e.target.value)}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />

      <Divider textAlign="left">
        <Typography variant="subtitle2">Шаблон спавнящейся цепочки</Typography>
      </Divider>
      <ChainBuilder
        key={isEdit ? `builder-${catalog.actions.length > 0}` : undefined}
        mode="template"
        value={initialBuilderState}
        extraHints={TRIGGER_TEMPLATE_HINTS}
        onChange={(_, tpl) => setTemplate(tpl)}
      />

      {canWrite && (
        <Stack direction="row" justifyContent="flex-end">
          <Button variant="contained" onClick={handleSubmit} disabled={loading}>
            Сохранить
          </Button>
        </Stack>
      )}
    </Stack>
  )
}
